import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import styles from "./Republish.module.scss";
import { useTranslation } from "react-i18next";
import SpinnerArea from "../../components/SpinnerArea";
import MainContext from "../../context/mainContext";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import workflowService from "../../services/workflow.service";
import { RepublishModel, RepublishResult } from "../../models/republish.model";
import moment from "moment";
import PlaceholderControl from "../../components/PlaceholderControl";
import FileUploader, { FileWrapper } from "../../components/FileUploader";
import BootstrapDialogTitle from "../../components/BootstrapDialogTitle";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const Republish: React.FC = () => {
  const { ticketNumber } = useParams();
  const { setBanner, service } = useContext(MainContext);

  const navigate = useNavigate();

  const [data, setData] = useState<RepublishModel>();
  const [loading, setLoading] = useState(false);
  const [remoteError, setRemoteError] = useState(false);
  const [remoteErrorMessage, setRemoteErrorMessage] = useState<string>();

  const [selectedFinalDocument, setSelectedFinalDocument] = useState<string>();
  const [uploadCovers, setUploadCovers] = useState<FileWrapper[]>([]);

  const [republishResult, setRepublishResult] = useState<RepublishResult>();

  const { t } = useTranslation();

  useEffect(() => {
    setBanner("rocky-peak-sunset.png" );
  });

  useEffect(() => {
    service(() => workflowService.republishInfo(ticketNumber!), {
      callback: (data) => {
        if (data) {
          setData(data);
        }
      },
      loader: setLoading,
      handleError: (error: any) => {
        if ("response" in error && error.response.status === 403) {
          setRemoteError(true);
        } else if (error && error.response && error.response.data?.message) {
          setRemoteError(true);
          setRemoteErrorMessage(error.response.data?.message);
        }
      },
    });
  }, [ticketNumber]);

  const handleRepublish = () => {
    if (data) {
      service(
        () =>
          workflowService.republishSave(
            data.ticketNumber,
            { ...data },
            uploadCovers
          ),
        {
          loader: setLoading,
          callback: (data: RepublishResult) => {
            setRepublishResult(data);
          },
        }
      );
    }
  };

  return (
    <SpinnerArea loading={loading}>
      <Box className={styles.republishView}>
        <div className={styles.republishTitle}>
          <h2>{t("publicationProcedures")}</h2>
        </div>
        <Paper className={styles.main}>
          {remoteError && (
            <h2 className={styles.unauthorizedMessage}>
              <ErrorOutlineIcon /> {t(remoteErrorMessage || "unauthorized")}
            </h2>
          )}
          {data && (
            <>
              <Box className={styles.republishHeader}>
                <p>
                  <strong>{t("publicationTicketNumber")}: </strong>
                  {data?.ticketNumber}
                </p>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.title}>
                        {t("title")}
                      </TableCell>
                      <TableCell className={styles.createdBy}>
                        {t("createdBy")}
                      </TableCell>
                      <TableCell className={styles.createdDate}>
                        {t("createdDate")}
                      </TableCell>
                      <TableCell className={styles.description}>
                        {t("description")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className={styles.title}>
                        {data.title}
                      </TableCell>
                      <TableCell className={styles.createdBy}>
                        {data.createdBy}
                      </TableCell>
                      <TableCell className={styles.createdDate}>
                        {moment(data.createdDate).format("MM/DD/YYYY")}
                      </TableCell>
                      <TableCell className={styles.description}>
                        <p>{data?.abstract}</p>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
              <Box className={styles.republishForm}>
                <p>{t("republishInstructions")}</p>

                <div className={styles.uploadSection}>
                  <FormControl fullWidth>
                    <FormLabel id="selectedFinalDocument-label">
                      {t("selectFileToReplace")}
                    </FormLabel>
                    <Select
                      id="selectedFinalDocument"
                      name="selectedFinalDocument"
                      fullWidth
                      value={selectedFinalDocument || ""}
                      onChange={(event: SelectChangeEvent) => {
                        setSelectedFinalDocument(event.target.value);
                      }}
                      displayEmpty
                      renderValue={(value) =>
                        value !== "" ? (
                          data.currentDocuments.find(
                            (x) => x.language === value
                          )?.name
                        ) : (
                          <PlaceholderControl text={t("selectFileToReplace")} />
                        )
                      }
                    >
                      <MenuItem
                        key={`languages_${0}`}
                        value={0}
                        className={"selectPlaceholder"}
                      >
                        {t("selectFileToReplace")}
                      </MenuItem>
                      {data.currentDocuments?.map((x) => (
                        <MenuItem key={`languages_${x.id}`} value={x.language}>
                          {x.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FileUploader
                    customClassName={styles["upload-covers"]}
                    onChange={(file: File | File[] | FileWrapper[]) => {
                      setUploadCovers(file as FileWrapper[]);

                      setSelectedFinalDocument(undefined);
                    }}
                    allowedExtensions={[".pdf"]}
                    isReadOnly={!selectedFinalDocument}
                    title={t("uploadNewFile")!}
                    appendMode
                    prefix={selectedFinalDocument}
                  />
                </div>

                <div className={styles.formActions}>
                  <Button
                    className={styles.cancel}
                    onClick={() => navigate(-1)}
                  >
                    {t("cancel")}
                  </Button>

                  <Button
                    className={styles.republish}
                    disabled={!uploadCovers || uploadCovers.length === 0}
                    onClick={handleRepublish}
                  >
                    {t("republish")}
                  </Button>
                </div>
              </Box>
              {republishResult && (
                <RepublishResultDialog
                  result={republishResult}
                  onClose={() => setRepublishResult(undefined)}
                />
              )}
            </>
          )}
        </Paper>
      </Box>
    </SpinnerArea>
  );
};

const RepublishResultDialog: React.FC<{
  result: RepublishResult;
  onClose: () => void;
}> = ({ result, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Dialog
      className={styles.republishDialog}
      open={Boolean(result)}
      fullWidth
      maxWidth="sm"
    >
      <BootstrapDialogTitle onClose={onClose}></BootstrapDialogTitle>
      <DialogContent>
        {result.success ? (
          <>
            <p>{t("republished")}</p>
            <CheckCircleIcon />
          </>
        ) : (
          <>
            <p>{result.message}</p>
            <ErrorOutlineIcon className={styles.error} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={styles.no}
          variant="contained"
          onClick={() => {
            if (result.success) navigate(-1);
            else onClose();
          }}
        >
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Republish;
